import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer } from "lucide-react";
import { toast } from "sonner";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const fmtDT = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const labelInfo = (r) => ({ petugas: r.penerima, barang: r.material_name, waktu: fmtDT(r.created_at), tracking: r.tracking_no, status: r.status });

function oneLabel(d, big) {
  return `<div class="lbl"><div class="row">Petugas: <b>${esc(d.petugas)}</b></div><div class="row">Barang: <b>${esc(d.barang)}</b></div>
    <div class="row">Tanggal: <b>${esc(d.waktu)}</b></div><div class="trk">${esc(d.tracking)}</div><div class="st">${esc(d.status)}</div></div>`;
}

function printHtml(d, w, copies) {
  const big = w > 60;
  return `<html><head><title>Label ${esc(d.tracking)}</title><style>
    @page { size: ${w}mm auto; margin: 2mm; } body { margin: 0; font-family: Arial, sans-serif; color: #000; }
    .lbl { width: ${w - 4}mm; page-break-after: always; padding: 1mm 0; } .lbl:last-child { page-break-after: auto; }
    .row { font-size: ${big ? 12 : 10}px; margin: 1px 0; } .trk { font-size: ${big ? 26 : 20}px; font-weight: 900; text-align: center; margin: 3px 0; letter-spacing: 1px; border: 2px solid #000; padding: 2px 0; }
    .st { text-align: center; font-weight: bold; font-size: ${big ? 13 : 11}px; }
  </style></head><body>${Array.from({ length: copies }, () => oneLabel(d, big)).join("")}
  <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script></body></html>`;
}

export function ReceiptLabelDialog({ receipt, onClose }) {
  const [size, setSize] = useState("58");
  const [copies, setCopies] = useState(1);
  if (!receipt) return null;
  const d = labelInfo(receipt);
  function print() {
    const n = Math.min(100, Math.max(1, parseInt(copies, 10) || 1));
    const win = window.open("", "_blank", "width=420,height=600");
    if (!win) { toast.error("Pop-up diblokir browser. Izinkan pop-up untuk mencetak."); return; }
    win.document.write(printHtml(d, Number(size), n));
    win.document.close();
  }
  return (
    <Dialog open={!!receipt} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="rlabel-dialog">
        <DialogHeader><DialogTitle>Cetak Label Bahan Masuk</DialogTitle></DialogHeader>
        <div className="border-2 border-dashed rounded p-3 bg-white text-black space-y-0.5 text-sm" data-testid="rlabel-preview">
          <div>Petugas: <b>{d.petugas}</b></div><div>Barang: <b>{d.barang}</b></div><div>Tanggal: <b>{d.waktu}</b></div>
          <div className="text-2xl font-black text-center border-2 border-black my-1 tracking-wide" data-testid="rlabel-tracking">{d.tracking}</div>
          <div className="text-center font-bold" data-testid="rlabel-status">{d.status}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Ukuran</Label>
            <Select value={size} onValueChange={setSize}><SelectTrigger className="mt-1" data-testid="rlabel-size"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="58">58 mm</SelectItem><SelectItem value="80">80 mm</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Jumlah cetak</Label><Input type="number" min={1} max={100} className="mt-1" value={copies} onChange={(e) => setCopies(e.target.value)} data-testid="rlabel-copies" /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="rlabel-close">Tutup</Button>
          <Button onClick={print} data-testid="rlabel-print"><Printer className="w-4 h-4 mr-1" /> Cetak</Button>
        </div>
        <p className="text-xs text-muted-foreground">Pilih printer thermal Bluetooth yang sudah terpasang di dialog cetak.</p>
      </DialogContent>
    </Dialog>
  );
}
